"use client";

import { useConsentData } from "@/components/third-party/TruendoCookieConsent/TruendoConsentProvider";
import { useHashDetection } from "@/hooks/useHashDetection";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";

export function SLHomeEntryOverlay({ video }: { video: React.ReactNode }) {
  const theme = useTheme();
  const [showContent, setShowContent] = useState(false);
  const { truendoBannerDismissed, truendoBlocked } = useConsentData();
  const entryAnimationReady = truendoBannerDismissed || truendoBlocked;
  const { hash, hasInitialHash } = useHashDetection();

  useEffect(() => {
    if (!entryAnimationReady) {
      return;
    }

    const contentTimer = setTimeout(() => {
      setShowContent(true);
    }, 100);

    const preventScroll = (e: Event) => {
      e.preventDefault();
    };

    window.addEventListener("wheel", preventScroll, { passive: false });
    window.addEventListener("touchmove", preventScroll, { passive: false });
    const preventKeyScroll = (e: KeyboardEvent) => {
      if ([32, 33, 34, 35, 36, 37, 38, 39, 40].includes(e.keyCode)) {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", preventKeyScroll);

    const timer = setTimeout(() => {
      window.removeEventListener("wheel", preventScroll);
      window.removeEventListener("touchmove", preventScroll);
      window.removeEventListener("keydown", preventKeyScroll);
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(contentTimer);
      window.removeEventListener("wheel", preventScroll);
      window.removeEventListener("touchmove", preventScroll);
      window.removeEventListener("keydown", preventKeyScroll);
    };
  }, [entryAnimationReady]);

  useEffect(() => {
    if (!entryAnimationReady || !hasInitialHash || !hash) {
      return;
    }

    const timer = setTimeout(() => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 3100);

    return () => clearTimeout(timer);
  }, [entryAnimationReady, hasInitialHash, hash]);

  useEffect(() => {
    if (hash && !hasInitialHash) {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash, hasInitialHash]);

  if (!entryAnimationReady) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 9998,
          backgroundColor: theme.palette.primary.main,
          pointerEvents: "none",
        }}
      />
    );
  }

  return (
    <AnimatePresence>
      {!showContent && (
        <Box
          component={motion.div}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          width="100vw"
          height="100vh"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 9999,
            backgroundColor: theme.palette.primary.main,
            pointerEvents: "none",
          }}
        >
          {video}
        </Box>
      )}
    </AnimatePresence>
  );
}
